import { formatFixMetric } from "../lib/fixMetrics.mjs";
import { motion, AnimatePresence } from "framer-motion";
import {
  ListChecks, Check, RotateCcw, Trash2, ArrowUp, ArrowDown, Minus, CircleCheck, Clock,
} from "lucide-react";

const METRICS = [
  { key: "clicks", label: "Clicks", better: "up" },
  { key: "users", label: "Users", better: "up" },
  { key: "conversions", label: "Key events", better: "up" },
  { key: "ctr", label: "CTR", better: "up", suffix: "%" },
];

const Delta = ({ base, cur, better, suffix }) => {
  if (base === undefined || cur === undefined || base === null || cur === null)
    return <span className="text-slate-500">—</span>;
  const diff = +(cur - base).toFixed(1);
  const pct = base ? Math.round((diff / base) * 100) : 0;
  const flat = diff === 0;
  const good = better === "up" ? diff > 0 : diff < 0;
  const Icon = flat ? Minus : diff > 0 ? ArrowUp : ArrowDown;
  const color = flat ? "text-slate-500" : good ? "text-emerald-700" : "text-amber-700";
  return (
    <span className={`inline-flex items-center gap-0.5 font-semibold ${color}`}>
      <Icon size={12} />{flat ? "0" : `${Math.abs(pct)}%`}
    </span>
  );
};

const isToday = (iso) => {
  try { return new Date(iso).toDateString() === new Date().toDateString(); } catch { return false; }
};

export default function FixTracker({ fixes, onToggleDone, onRemove, busyId }) {
  if (!fixes || fixes.length === 0) return null;
  return (
    <section data-testid="fix-tracker" className="mt-8 md:mt-12">
      <div className="flex items-center gap-2">
        <div className="h-6 w-1 rounded-full bg-emerald-600" aria-hidden="true" />
        <h2 className="font-heading text-h2-compact text-navy-950">Fixes you're tracking</h2>
        <span className="tabular ml-1 rounded-full bg-offwhite px-2 py-0.5 text-xs font-bold text-slate-600">{fixes.length}</span>
      </div>
      <p className="mt-1 text-small text-slate-600">Tick a fix off once you've done it — SEO Signals watches the numbers behind it move from your baseline.</p>

      <div className="mt-4 space-y-3">
        <AnimatePresence initial={false}>
          {fixes.map((f) => {
            const done = f.status === "done";
            const b = f.baseline || {};
            const c = f.current || {};
            return (
              <motion.div
                key={f.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                data-testid={`fix-item-${f.id}`}
                className={`rounded-card border p-4 md:p-5 ${done ? "border-emerald-100 bg-emerald-50" : "border-line bg-white"}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {done && <CircleCheck size={16} className="shrink-0 text-emerald-700" aria-hidden="true" />}
                      <h3 className={`font-heading text-h3 text-navy-950 ${done ? "line-through decoration-emerald-600/50" : ""}`}>{f.headline}</h3>
                    </div>
                    <p className="mt-1 flex items-start gap-1.5 text-small text-slate-600"><ListChecks size={15} className="mt-0.5 shrink-0 text-slate-500" aria-hidden="true" />{f.action}</p>
                    <div className="mt-1.5 flex items-center gap-2 text-[12px] text-slate-600">
                      <Clock size={12} aria-hidden="true" />
                      {isToday(f.baseline_at)
                        ? "Baseline captured today — check back over the next days to see movement"
                        : `Baseline captured ${new Date(f.baseline_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => onToggleDone(f)}
                      disabled={busyId === f.id}
                      data-testid={`fix-toggle-${f.id}`}
                      className={`inline-flex tap-safe items-center gap-1.5 rounded-btn px-3 text-small font-semibold transition-colors duration-control ease-editorial focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 lg:min-h-0 lg:h-control ${done ? "border border-line-strong text-navy-800 hover:bg-offwhite" : "bg-emerald-700 text-white hover:bg-emerald-800"}`}
                    >
                      {done ? <><RotateCcw size={13} /> Reopen</> : <><Check size={13} /> Mark done</>}
                    </button>
                    <button
                      onClick={() => onRemove(f)}
                      disabled={busyId === f.id}
                      data-testid={`fix-remove-${f.id}`}
                      className="tap-safe grid w-11 place-items-center rounded-btn border border-line text-slate-600 transition-colors duration-control hover:border-red-600/40 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 lg:w-control"
                      aria-label="Remove fix"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 border-t border-line pt-3 md:grid-cols-4">
                  {METRICS.map((m) => (
                    <div key={m.key}>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-600">{m.label}</div>
                      <div className="mt-0.5 flex items-baseline gap-2">
                        <span className="tabular font-heading font-semibold text-navy-950">
                          {formatFixMetric(c[m.key], m.suffix)}
                        </span>
                        <Delta base={b[m.key]} cur={c[m.key]} better={m.better} suffix={m.suffix} />
                      </div>
                      <div className="tabular text-[12px] text-slate-600">from {formatFixMetric(b[m.key], m.suffix)}</div>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </section>
  );
}
